import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  LogOut,
  Plus,
  Pencil,
  Trash2,
  MapPin,
  Home,
  Sparkles,
  Inbox,
  Check,
  ExternalLink,
  Ticket,
  Download,
  QrCode,
  Send,
  Users,
  Calendar,
  KeyRound,
  Copy,
  RefreshCw,
  Printer,
  HeartHandshake,
} from "lucide-react";
import { toast } from "sonner";
import { api, downloadCsv, downloadFile, formatApiError } from "../lib/api";
import { useAdminAuth } from "../context/AdminAuth";
import { EXPERIENCES as EXP_DATA } from "../data/experiences";
import { Logo } from "../components/site/Logo";

const PARTENAIRES_LISTE = [...new Set(EXP_DATA.flatMap((e) => (e.offres || []).map((o) => o.partenaire)))].sort();
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

const EMPTY_LIEU = {
  name: "",
  type: "hebergement",
  commune: "",
  secteur: "",
  description: "",
  experiences: "",
  capacite: "",
  conditions: "",
  contribution: "",
  periode: "",
  unique: "",
  esprit: "",
  comprend: "",
  petits_plus: "",
  photos: "",
  email: "contact@plusbellelavie971.com",
  lat: 16.22,
  lng: -61.55,
  active: true,
};

const LIST_FIELDS = ["experiences", "conditions", "contribution", "esprit", "comprend", "petits_plus", "photos"];

const field =
  "rounded-xl border border-pblv-ink/15 bg-pblv-bg px-4 py-2.5 text-sm text-pblv-ink outline-none transition-colors focus:border-pblv-mauve w-full";

const mbtn = "inline-flex h-10 flex-1 basis-[45%] items-center justify-center gap-1.5 rounded-xl border px-2 text-sm font-semibold transition-colors";

function TestimonialDialog({ open, onOpenChange, initial, onSaved }) {
  const EMPTY = { name: "", role: "", quote: "", videoUrl: "", coverImageUrl: "", order: 0, active: true };
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initial]);

  const up = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: k === "order" ? parseInt(v || 0, 10) : v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/testimonials/${initial.id}`, form);
        toast.success("Témoignage mis à jour");
      } else {
        await api.post("/admin/testimonials", form);
        toast.success("Témoignage ajouté");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="admin-testimonial-dialog" className="max-h-[90vh] overflow-y-auto bg-pblv-bg sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-pblv-ink">
            {editing ? "Modifier le témoignage" : "Ajouter un témoignage"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Prénom</label>
            <input required value={form.name} onChange={up("name")} data-testid="testimonial-name" className={field} placeholder="Marie-Line" />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Rôle</label>
            <input required value={form.role} onChange={up("role")} data-testid="testimonial-role" className={field} placeholder="Bénéficiaire, Basse-Terre" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Citation</label>
            <textarea required rows={3} value={form.quote} onChange={up("quote")} data-testid="testimonial-quote" className={field} placeholder="J'avais oublié ce que voulait dire prendre soin de moi…" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">URL vidéo (YouTube, Vimeo, mp4)</label>
            <input value={form.videoUrl} onChange={up("videoUrl")} data-testid="testimonial-videoUrl" className={field} placeholder="https://youtu.be/…  (laisser vide pour afficher « Vidéo à venir »)" />
          </div>
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Photo cover</label>
            <input value={form.coverImageUrl} onChange={up("coverImageUrl")} data-testid="testimonial-cover" className={field} placeholder="https://images.pexels.com/…" />
            {form.coverImageUrl && (
              <img src={form.coverImageUrl} alt="cover" className="mt-2 h-24 w-40 rounded-lg object-cover" />
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Ordre d'affichage</label>
            <input type="number" value={form.order} onChange={up("order")} data-testid="testimonial-order" className={field} />
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm font-medium text-pblv-ink">
            <input type="checkbox" checked={form.active} onChange={up("active")} data-testid="testimonial-active" className="h-4 w-4 rounded" />
            Visible sur le site
          </label>
          <button
            type="submit"
            disabled={saving}
            data-testid="testimonial-save"
            className="col-span-full mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter le témoignage"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function LieuDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_LIEU);
  const [saving, setSaving] = useState(false);
  const editing = !!initial?.id;

  useEffect(() => {
    if (open) {
      if (initial) {
        const f = { ...EMPTY_LIEU, ...initial };
        LIST_FIELDS.forEach((k) => {
          f[k] = (initial[k] || []).join(", ");
        });
        setForm(f);
      } else {
        setForm(EMPTY_LIEU);
      }
    }
  }, [open, initial]);

  const up = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toList = (s) =>
    String(s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      type: form.type,
      commune: form.commune,
      secteur: form.secteur,
      description: form.description,
      capacite: form.capacite,
      periode: form.periode,
      unique: form.unique,
      email: form.email,
      lat: parseFloat(form.lat),
      lng: parseFloat(form.lng),
      active: !!form.active,
    };
    LIST_FIELDS.forEach((k) => {
      payload[k] = toList(form[k]);
    });
    try {
      if (editing) await api.put(`/admin/lieux/${initial.id}`, payload);
      else await api.post("/admin/lieux", payload);
      toast.success(editing ? "Lieu mis à jour" : "Lieu ajouté");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error("Enregistrement impossible", {
        description: formatApiError(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="lieu-dialog"
        className="max-h-[90vh] overflow-y-auto rounded-3xl bg-pblv-bg sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-medium text-pblv-ink">
            {editing ? "Modifier le lieu" : "Ajouter une T'POZ"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Nom</label>
              <input data-testid="lieu-name" className={field} required value={form.name} onChange={up("name")} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Type</label>
              <select data-testid="lieu-type" className={field} value={form.type} onChange={up("type")}>
                <option value="hebergement">Hébergement</option>
                <option value="bien-etre">Bien-être</option>
                <option value="les-deux">Hébergement & Bien-être</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Commune</label>
              <input data-testid="lieu-commune" className={field} required value={form.commune} onChange={up("commune")} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Secteur</label>
              <input data-testid="lieu-secteur" className={field} value={form.secteur} onChange={up("secteur")} placeholder="Grande-Terre, Basse-Terre…" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Description</label>
            <textarea data-testid="lieu-description" rows={2} className={field} value={form.description} onChange={up("description")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Expériences proposées (virgules)</label>
            <input data-testid="lieu-experiences" className={field} value={form.experiences} onChange={up("experiences")} placeholder="Nuitée, Massage, Yoga…" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Pour combien de personnes ?</label>
              <input data-testid="lieu-capacite" className={field} value={form.capacite} onChange={up("capacite")} placeholder="1 à 2 personnes" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Période de validité</label>
              <input data-testid="lieu-periode" className={field} value={form.periode} onChange={up("periode")} placeholder="Toute l'année" />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Conditions de réservation (virgules)</label>
            <input data-testid="lieu-conditions" className={field} value={form.conditions} onChange={up("conditions")} placeholder="Réservation obligatoire, 7 jours avant…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Avantage T'POZ (virgules)</label>
            <input data-testid="lieu-contribution" className={field} value={form.contribution} onChange={up("contribution")} placeholder="Massage offert, remise 20 %…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">L'esprit de cette T'POZ (mots-clés, virgules)</label>
            <input data-testid="lieu-esprit" className={field} value={form.esprit} onChange={up("esprit")} placeholder="Se reposer, Respirer, Se reconnecter" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Ce que comprend cette T'POZ (virgules)</label>
            <input data-testid="lieu-comprend" className={field} value={form.comprend} onChange={up("comprend")} placeholder="Hébergement 2 nuits, Petit-déjeuner…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Les petits plus (virgules)</label>
            <input data-testid="lieu-petits-plus" className={field} value={form.petits_plus} onChange={up("petits_plus")} placeholder="Piscine, Jardin, Wifi…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Ce qui rend ce lieu unique</label>
            <textarea data-testid="lieu-unique" rows={2} className={field} value={form.unique} onChange={up("unique")} placeholder="Vue mer, environnement calme…" />
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-pblv-ink">Photos (URLs séparées par des virgules)</label>
            <textarea data-testid="lieu-photos" rows={2} className={field} value={form.photos} onChange={up("photos")} placeholder="https://…, https://…" />
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">E-mail</label>
              <input data-testid="lieu-email" type="email" className={field} required value={form.email} onChange={up("email")} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Latitude</label>
              <input data-testid="lieu-lat" type="number" step="any" className={field} required value={form.lat} onChange={up("lat")} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-pblv-ink">Longitude</label>
              <input data-testid="lieu-lng" type="number" step="any" className={field} required value={form.lng} onChange={up("lng")} />
            </div>
          </div>
          <label className="flex items-center gap-3 text-sm font-medium text-pblv-ink">
            <input data-testid="lieu-active" type="checkbox" checked={form.active} onChange={up("active")} className="h-4 w-4 accent-pblv-mauve" />
            Visible sur la carte publique
          </label>
          <button
            type="submit"
            data-testid="lieu-save"
            disabled={saving}
            className="mt-1 inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3 text-sm font-semibold text-white transition-all hover:bg-pblv-mauvedark disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : editing ? "Enregistrer" : "Ajouter"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const StatCard = ({ label, value, icon: Icon, tint, sub, testid }) => (
  <div className="rounded-2xl border border-pblv-ink/10 bg-white p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-pblv-inksoft">
        {label}
      </p>
      <Icon className="h-4 w-4" style={{ color: tint }} strokeWidth={1.8} />
    </div>
    <p className="mt-3 font-serif text-4xl font-semibold text-pblv-ink">{value}</p>
    {sub && <p className="mt-1.5 text-xs text-pblv-inksoft">{sub}</p>}
  </div>
);

function MonthlyChart({ months }) {
  if (!months || months.length === 0) return null;
  const max = Math.max(1, ...months.map((m) => Math.max(m.demandes, m.partenaires)));
  const totalD = months.reduce((s, m) => s + m.demandes, 0);
  const totalP = months.reduce((s, m) => s + m.partenaires, 0);
  return (
    <div
      data-testid="admin-monthly-chart"
      className="mt-8 rounded-2xl border border-pblv-ink/10 bg-white p-5"
    >
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-pblv-mauve">
            Activité — 6 derniers mois
          </p>
          <h3 className="mt-1 font-serif text-xl font-medium text-pblv-ink">
            Évolution des sollicitations
          </h3>
        </div>
        <div className="flex items-center gap-5 text-xs font-semibold text-pblv-inksoft">
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-pblv-mauve" /> Demandes bénéficiaires ({totalD})
          </span>
          <span className="inline-flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-pblv-sage" /> Candidatures ({totalP})
          </span>
        </div>
      </div>
      <div className="grid h-28 grid-cols-6 items-end gap-2 md:gap-4">
        {months.map((m) => (
          <div key={m.key} className="flex h-full flex-col items-center gap-2" data-testid={`chart-bar-${m.key}`}>
            <div className="flex flex-1 w-full items-end justify-center gap-1.5">
              <div
                className="w-3 min-h-[2px] max-w-[26px] rounded-t-md bg-pblv-mauve transition-all duration-500 md:w-5"
                style={{ height: `${Math.max((m.demandes / max) * 100, m.demandes > 0 ? 8 : 0)}%` }}
                title={`${m.demandes} demande(s)`}
              />
              <div
                className="w-3 min-h-[2px] max-w-[26px] rounded-t-md bg-pblv-sage transition-all duration-500 md:w-5"
                style={{ height: `${Math.max((m.partenaires / max) * 100, m.partenaires > 0 ? 8 : 0)}%` }}
                title={`${m.partenaires} candidature(s)`}
              />
            </div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-pblv-inksoft">
              {m.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);
  const { user, logout } = useAdminAuth();
  const [tab, setTab] = useState("lieux");
  const [stats, setStats] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [lieux, setLieux] = useState([]);
  const [partenaires, setPartenaires] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [eligibilites, setEligibilites] = useState([]);
  const [bons, setBons] = useState([]);
  const [codeDemandes, setCodeDemandes] = useState([]);
  const [partCodes, setPartCodes] = useState([]);
  const [partSearch, setPartSearch] = useState("");
  const [partFiche, setPartFiche] = useState(null);
  const [messages, setMessages] = useState([]);
  const [accesList, setAccesList] = useState([]);
  const [plafondUsage, setPlafondUsage] = useState({});
  const [plafondVals, setPlafondVals] = useState({});
  const [plafondSaved, setPlafondSaved] = useState({});
  const [codeFilter, setCodeFilter] = useState("all");
  const [subscribers, setSubscribers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialDialog, setTestimonialDialog] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const refresh = useCallback(async () => {
    const endpoints = [
      "/admin/stats",
      "/admin/lieux",
      "/admin/partenaires",
      "/admin/demandes",
      "/admin/stats/monthly",
      "/admin/newsletter",
      "/admin/testimonials",
      "/admin/eligibilites",
      "/admin/bons",
      "/admin/code-demandes",
      "/admin/acces",
      "/admin/plafonds",
      "/admin/partenaires-codes",
      "/admin/contact-messages",
    ];
    // Chaque appel est indépendant : un échec isolé ne vide plus tout le tableau de bord.
    const results = await Promise.allSettled(endpoints.map((e) => api.get(e)));
    const val = (i, fallback) => (results[i].status === "fulfilled" ? results[i].value.data : fallback);
    const [s, l, p, d, m, n, tt, el, bo, cd, ac, pf, pc, cm] = [
      val(0, null), val(1, []), val(2, []), val(3, []), val(4, null), val(5, []), val(6, []),
      val(7, []), val(8, []), val(9, []), val(10, []), val(11, null), val(12, []), val(13, []),
    ];
    if (s) setStats(s);
    setLieux(l);
    setPartenaires(p);
    setDemandes(d);
    setMonthly(m?.months || []);
    setSubscribers(n);
    setTestimonials(tt);
    setEligibilites(el);
    setBons(bo);
    setCodeDemandes(cd);
    setPartCodes(pc || []);
    setMessages(cm || []);
    setAccesList(ac);
    if (pf) {
      const caps = {};
      (pf?.plafonds || []).forEach((c) => {
        caps[c.partenaire] = String(c.max_mois ?? "");
      });
      setPlafondVals(caps);
      setPlafondSaved(caps);
      setPlafondUsage(pf?.usage || {});
    }
    const failed = endpoints.filter((_, i) => results[i].status === "rejected");
    if (failed.length) {
      const err = results[endpoints.indexOf(failed[0])].reason;
      toast.error(`Certaines données n'ont pas pu être chargées (${failed.length}/${endpoints.length})`, {
        description: formatApiError(err?.response?.data?.detail) || err?.message || failed.join(", "),
      });
    }
  }, []);

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Supprimer ce témoignage ?")) return;
    try {
      await api.delete(`/admin/testimonials/${id}`);
      toast.success("Témoignage supprimé");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    refresh();
  }, [refresh]);

  const deleteLieu = async (id) => {
    if (!window.confirm("Supprimer ce lieu ?")) return;
    try {
      await api.delete(`/admin/lieux/${id}`);
      toast.success("Lieu supprimé");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/partenaires/${id}`, { status });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deletePartenaire = async (id) => {
    if (!window.confirm("Supprimer cette candidature ?")) return;
    try {
      await api.delete(`/admin/partenaires/${id}`);
      toast.success("Candidature supprimée");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setDemandeStatus = async (id, status, scheduledDate) => {
    try {
      const body = { status };
      if (scheduledDate !== undefined) body.scheduled_date = scheduledDate;
      await api.patch(`/admin/demandes/${id}`, body);
      toast.success("Statut mis à jour");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const validateDemande = async (id) => {
    const suggested = new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10);
    const input = window.prompt(
      "Date prévue de la T'POZ (AAAA-MM-JJ)\nUn rappel sera envoyé automatiquement 7 jours avant.\nLaisser vide pour valider sans date.",
      suggested,
    );
    if (input === null) return;
    const trimmed = input.trim();
    if (trimmed && !/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      toast.error("Format de date invalide (AAAA-MM-JJ)");
      return;
    }
    setDemandeStatus(id, "validée", trimmed || null);
  };

  const deleteDemande = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/admin/demandes/${id}`);
      toast.success("Demande supprimée");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setEligStatus = async (id, status) => {
    try {
      await api.patch(`/admin/eligibilites/${id}`, { status });
      toast.success("Statut mis à jour");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteElig = async (id) => {
    if (!window.confirm("Supprimer cette demande d'éligibilité ?")) return;
    try {
      await api.delete(`/admin/eligibilites/${id}`);
      toast.success("Demande supprimée");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const validerElig = async (id) => {
    try {
      const { data } = await api.post(`/admin/eligibilites/${id}/valider`);
      toast.success("Éligibilité validée", { description: `Numéro de dossier : ${data.dossier}` });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyBonLink = async (dossier) => {
    const url = `${window.location.origin}/recevoir-mon-bon?dossier=${dossier}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Lien copié", { description: url });
    } catch {
      window.prompt("Copiez le lien :", url);
    }
  };

  const setBonStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bons/${id}`, { status });
      toast.success("Statut mis à jour");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteBon = async (id) => {
    if (!window.confirm("Supprimer cette demande de bon ?")) return;
    try {
      await api.delete(`/admin/bons/${id}`);
      toast.success("Demande supprimée");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const EMPTY_BON_PDF = { type_partenaire: "hebergeur", modalite: "remise", tarif_normal: "", remise_pct: "", montant_dedie: "", reste_a_charge: "" };
  const [bonPdfFor, setBonPdfFor] = useState(null);
  const [bonPdfForm, setBonPdfForm] = useState(EMPTY_BON_PDF);
  const [bonPdfLoading, setBonPdfLoading] = useState(false);

  const openBonPdf = (bon) => {
    setBonPdfForm(EMPTY_BON_PDF);
    setBonPdfFor(bon);
  };

  const generateBonPdf = async (e) => {
    e.preventDefault();
    setBonPdfLoading(true);
    try {
      await downloadFile(`/admin/bons/${bonPdfFor.id}/pdf`, `bon-tpoz-${bonPdfFor.numero_dossier}.pdf`, bonPdfForm);
      toast.success("Bon PDF généré", { description: "Prêt à être envoyé à la bénéficiaire." });
      setBonPdfFor(null);
    } catch (err) {
      toast.error("Génération impossible", { description: err.message });
    } finally {
      setBonPdfLoading(false);
    }
  };

  const EMPTY_ATTRIB = { prenom: "", nom: "", email: "", tel: "" };
  const [attribFor, setAttribFor] = useState(null);
  const [attribForm, setAttribForm] = useState(EMPTY_ATTRIB);
  const [attribLoading, setAttribLoading] = useState(false);

  const submitAttribution = async (e) => {
    e.preventDefault();
    setAttribLoading(true);
    try {
      await api.post(`/admin/code-demandes/${attribFor.id}/attribuer`, attribForm);
      toast.success("Heure suspendue attribuée", { description: "Le code et le bon T'POZ ont été envoyés à la bénéficiaire." });
      setAttribFor(null);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAttribLoading(false);
    }
  };

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ old_password: "", new_password: "", confirm: "" });
  const [pwdLoading, setPwdLoading] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password.length < 8) {
      toast.error("Le nouveau mot de passe doit contenir au moins 8 caractères.");
      return;
    }
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error("La confirmation ne correspond pas au nouveau mot de passe.");
      return;
    }
    setPwdLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: pwdForm.old_password, new_password: pwdForm.new_password });
      toast.success("Mot de passe changé", { description: "Utilisez-le dès votre prochaine connexion." });
      setPwdOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPwdLoading(false);
    }
  };

  const validerCode = async (id) => {
    try {
      const { data } = await api.post(`/admin/code-demandes/${id}/valider`);
      toast.success("Demande validée", { description: `Code ${data.code} envoyé par e-mail et SMS à la bénéficiaire.` });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const refuserCode = async (id) => {
    if (!window.confirm("Refuser cette demande ? Un e-mail neutre sera envoyé à la personne.")) return;
    try {
      await api.post(`/admin/code-demandes/${id}/refuser`);
      toast.success("Demande refusée", { description: "Un e-mail neutre a été envoyé." });
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const renvoyerCode = async (id) => {
    try {
      await api.post(`/admin/code-demandes/${id}/renvoyer`);
      toast.success("Code renvoyé par e-mail");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const annulerCode = async (id) => {
    if (!window.confirm("Annuler ce code ? Il ne sera plus valable.")) return;
    try {
      await api.post(`/admin/code-demandes/${id}/annuler`);
      toast.success("Code annulé");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const savePlafond = async (partenaire) => {
    const val = (plafondVals[partenaire] ?? "").trim();
    if (val === (plafondSaved[partenaire] ?? "")) return;
    try {
      await api.put("/admin/plafonds", { partenaire, max_mois: val ? Number(val) : null });
      setPlafondSaved((s) => ({ ...s, [partenaire]: val }));
      toast.success(val ? `Plafond de ${partenaire} : ${val} T'POZ / mois` : `Plafond retiré pour ${partenaire}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copyPartCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Code copié", { description: code });
    } catch {
      window.prompt("Copiez le code :", code);
    }
  };

  const regenPartCode = async (pc2) => {
    if (
      !window.confirm(
        `Régénérer le code de ${pc2.nom} ?\n\nL'ancien code ${pc2.code} et son QR code cesseront immédiatement de fonctionner.`
      )
    )
      return;
    try {
      const { data } = await api.post(`/admin/partenaires-codes/${pc2.code}/regenerer`);
      setPartCodes((prev) => prev.map((x) => (x.code === pc2.code ? { ...x, code: data.code } : x)));
      toast.success("Nouveau code généré", { description: `${data.nom} : ${data.code}` });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Régénération impossible");
    }
  };

  const toggleMessageTraite = async (msg) => {
    try {
      await api.patch(`/admin/contact-messages/${msg.id}/traiter`, { traite: !msg.traite });
      setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, traite: !msg.traite } : x)));
    } catch {
      toast.error("Action impossible");
    }
  };

  const codeMessage = (d) =>
    `T'POZ : votre demande est validée. Votre code : ${d.code}${d.valid_until ? `, valable jusqu'au ${new Date(d.valid_until).toLocaleDateString("fr-FR")}` : ""}. Réservez directement auprès de ${d.partenaire || "votre partenaire"} en respectant son délai de réservation, et donnez ce code. Infos : 0690 745 910`;

  const copySms = async (d) => {
    const sms = codeMessage(d);
    try {
      await navigator.clipboard.writeText(sms);
      toast.success("Message copié", { description: sms });
    } catch {
      window.prompt("Copiez le message :", sms);
    }
  };

  const exportCodes = () => {
    const statutLabel = (d) =>
      d.status === "validé" ? "Validée" : d.status === "refusé" ? "Refusée" : d.status === "annulé" ? "Code annulé" : d.status === "utilisé" ? "Venue confirmée" : "À traiter";
    const rows = [
      ["Prénom", "Nom", "E-mail", "Téléphone", "Expérience", "Statut", "Code", "Utilisations sur 12 mois", "Date de la demande"],
      ...codeDemandes.map((d) => [
        d.prenom || "",
        d.nom || "",
        d.email || "",
        d.tel || "",
        d.offre || "",
        statutLabel(d),
        d.code || "",
        `${Math.min(d.codes_par_mail ?? 0, 3)}/3`,
        d.created_at ? new Date(d.created_at).toLocaleDateString("fr-FR") : "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "demandes-tpoz.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAcces = () => {
    const rows = [
      ["Prénom", "E-mail", "Consentement", "Première expérience", "Dernière expérience", "Date"],
      ...accesList.map((a) => [
        a.prenom || "",
        a.email,
        a.consent ? "Oui" : "Non",
        a.experience_premiere || "",
        a.experience_derniere || "",
        a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : "",
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "acces-tpoz.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const deleteCodeDemande = async (id) => {
    if (!window.confirm("Supprimer cette demande de code ?")) return;
    try {
      await api.delete(`/admin/code-demandes/${id}`);
      toast.success("Demande supprimée");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const exportCsv = async (kind) => {
    const today = new Date().toISOString().slice(0, 10);
    try {
      await downloadCsv(
        `/admin/export/${kind}.csv`,
        `tpoz-${kind}-${today}.csv`,
      );
      toast.success("Export téléchargé");
    } catch (err) {
      toast.error("Export impossible", { description: err.message });
    }
  };

  const deleteSubscriber = async (id) => {
    if (!window.confirm("Supprimer cet abonné ?")) return;
    try {
      await api.delete(`/admin/newsletter/${id}`);
      toast.success("Abonné supprimé");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const [newsletterSubject, setNewsletterSubject] = useState("");
  const [newsletterBody, setNewsletterBody] = useState("");
  const [newsletterSending, setNewsletterSending] = useState(false);
  const [autoInfo, setAutoInfo] = useState(null);
  const [autoLoading, setAutoLoading] = useState(false);

  const loadAutoInfo = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/monthly/preview");
      setAutoInfo(data);
    } catch (err) {
      // silent
    }
  };

  const sendAutoNow = async () => {
    if (!window.confirm("Envoyer maintenant la newsletter automatique avec les nouveaux lieux du dernier mois ?")) return;
    setAutoLoading(true);
    try {
      const { data } = await api.post("/admin/newsletter/monthly/send-now");
      if (data.sent > 0) {
        toast.success(`Newsletter envoyée à ${data.sent} abonné·e·s (${data.count} nouveaux lieux)`);
      } else {
        toast.info("Aucun nouveau lieu ce mois-ci — envoi ignoré");
      }
      loadAutoInfo();
    } catch (err) {
      toast.error("Envoi impossible", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setAutoLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "newsletter") loadAutoInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const sendNewsletter = async (e) => {
    e.preventDefault();
    if (!newsletterSubject.trim() || !newsletterBody.trim()) return;
    if (!window.confirm(`Envoyer cet email à ${subscribers.filter(s => s.active).length} abonné·e·s ?`)) return;
    setNewsletterSending(true);
    try {
      const paragraphs = newsletterBody
        .split(/\n\n+/)
        .map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`)
        .join("");
      const html = `<div style="font-family:'Helvetica Neue',Arial,sans-serif;color:#2d2830;line-height:1.6;max-width:560px;margin:0 auto;padding:24px;background:#faf5ec">
        <h2 style="color:#b98524;margin:0 0 16px">${newsletterSubject}</h2>
        ${paragraphs}
        <hr style="border:none;border-top:1px solid #e8dfcf;margin:24px 0">
        <p style="font-size:12px;color:#6b6470;text-align:center">Newsletter Répit — Plus Belle La Vie 971 · T'POZ<br>Vous pouvez vous désabonner en répondant à cet e-mail.</p>
      </div>`;
      const { data } = await api.post("/admin/newsletter/broadcast", {
        subject: newsletterSubject,
        html,
      });
      toast.success(`Newsletter envoyée à ${data.sent} abonné·e·s`);
      setNewsletterSubject("");
      setNewsletterBody("");
    } catch (err) {
      toast.error("Envoi impossible", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setNewsletterSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-pblv-bg2">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-pblv-ink/10 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1300px] items-center justify-between gap-4 px-5 py-4 md:px-8">
          <Logo />
          <div className="flex items-center gap-3">
            <button
              onClick={async () => { setRefreshing(true); await refresh(); setRefreshing(false); toast.success("Données actualisées"); }}
              disabled={refreshing}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 px-4 py-2 text-sm font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Actualiser</span>
            </button>
            <button
              onClick={() => { setPwdForm({ old_password: "", new_password: "", confirm: "" }); setPwdOpen(true); }}
              data-testid="admin-change-password-btn"
              className="hidden items-center gap-2 rounded-full border border-pblv-ink/15 px-4 py-2 text-sm font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg sm:inline-flex"
            >
              <KeyRound className="h-4 w-4" /> Mot de passe
            </button>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-site"
              className="hidden items-center gap-2 rounded-full border border-pblv-ink/15 px-4 py-2 text-sm font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg sm:inline-flex"
            >
              <ExternalLink className="h-4 w-4" /> Voir le site
            </a>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full bg-pblv-ink px-4 py-2 text-sm font-semibold text-pblv-bg transition-colors hover:bg-pblv-mauve"
            >
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-5 py-10 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
            Tableau de bord
          </p>
          <h1 className="mt-2 font-serif text-4xl font-medium text-pblv-ink md:text-5xl">
            Bonjour, {user?.name?.split(" ")[0] || "Admin"}
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          <StatCard
            label="À traiter"
            testid="stat-a-traiter"
            value={stats?.a_traiter ?? "—"}
            icon={Inbox}
            tint="#D23F6F"
            sub="Demandes reçues, ni validées ni refusées"
          />
          <StatCard
            label="Demandes T'POZ"
            testid="stat-demandes-tpoz"
            value={(stats?.pour_soi ?? 0) + (stats?.offertes ?? 0)}
            icon={Ticket}
            tint="#D9A05B"
            sub={`Pour soi : ${stats?.pour_soi ?? 0} · Offertes : ${stats?.offertes ?? 0}`}
          />
          <StatCard label="Lieux" value={stats?.lieux ?? "—"} icon={MapPin} tint="#C6870F" />
          <StatCard label="Hébergement" value={stats?.hebergement ?? "—"} icon={Home} tint="#8FA07C" />
          <StatCard label="Bien-être" value={stats?.bien_etre ?? "—"} icon={Sparkles} tint="#C6870F" />
          <StatCard label="Candidatures partenaires" value={stats?.partenaires ?? "—"} icon={Inbox} tint="#B5762A" />
        </div>

        {/* Tabs */}
        <div className="mt-10 flex flex-wrap gap-2">
          {[
            { k: "lieux", label: "Les T'POZ" },
            { k: "demandes", label: `Demandes de bon${stats?.demandes_nouvelles ? ` (${stats.demandes_nouvelles})` : ""}` },
            { k: "codes", label: `Codes T'POZ${codeDemandes.filter((c) => c.status === "nouveau").length ? ` (${codeDemandes.filter((c) => c.status === "nouveau").length})` : ""}` },
            { k: "gestionpart", label: "Gestion partenaires" },
            { k: "acces", label: `Accès${accesList.length ? ` (${accesList.length})` : ""}` },
            { k: "messages", label: `Messages${messages.filter((mm) => !mm.traite).length ? ` (${messages.filter((mm) => !mm.traite).length})` : ""}` },
            { k: "eligibilite", label: `Éligibilité${eligibilites.filter((e) => e.status === "nouveau").length ? ` (${eligibilites.filter((e) => e.status === "nouveau").length})` : ""}` },
            { k: "bons", label: `Bons${bons.filter((b) => b.status === "a_traiter").length ? ` (${bons.filter((b) => b.status === "a_traiter").length})` : ""}` },
            { k: "partenaires", label: `Candidatures${stats?.partenaires_nouveaux ? ` (${stats.partenaires_nouveaux})` : ""}` },
            { k: "newsletter", label: `Newsletter${subscribers.length ? ` (${subscribers.length})` : ""}` },
            { k: "temoignages", label: `Témoignages${testimonials.length ? ` (${testimonials.length})` : ""}` },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                tab === t.k
                  ? "bg-pblv-ink text-pblv-bg"
                  : "bg-white text-pblv-ink hover:bg-pblv-bg"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Lieux tab */}
        {tab === "lieux" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <button
                data-testid="admin-add-lieu"
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
                className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
              >
                <Plus className="h-4 w-4" /> Ajouter un lieu
              </button>
            </div>
            <div className="space-y-3 md:hidden" data-testid="admin-lieux-cards">
              {lieux.map((l) => (
                <div key={l.id} data-testid={`admin-lieu-card-${l.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-pblv-ink">{l.name}</p>
                    <span className={`text-xs font-semibold ${l.active ? "text-emerald-600" : "text-pblv-inksoft"}`}>{l.active ? "Visible" : "Masqué"}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-pblv-inksoft">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${l.type === "bien-etre" ? "bg-pblv-mauve/15 text-pblv-mauvedark" : "bg-pblv-sage/20 text-[#5f7052]"}`}>
                      {l.type === "hebergement" ? "Hébergement" : l.type === "bien-etre" ? "Bien-être" : "Héberg. & Bien-être"}
                    </span>
                    {l.commune && <span>{l.commune}</span>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                    <button data-testid={`admin-edit-lieu-m-${l.id}`} onClick={() => { setEditing(l); setDialogOpen(true); }} className={`${mbtn} border-pblv-ink/10 text-pblv-ink hover:bg-pblv-bg`}>
                      <Pencil className="h-4 w-4" /> Modifier
                    </button>
                    <button data-testid={`admin-delete-lieu-m-${l.id}`} onClick={() => deleteLieu(l.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {lieux.length === 0 && <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucun lieu pour le moment.</p>}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
              <table className="w-full text-left text-sm" data-testid="admin-lieux-table">
                <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                  <tr>
                    <th className="px-5 py-3">Nom</th>
                    <th className="px-5 py-3">Type</th>
                    <th className="hidden px-5 py-3 md:table-cell">Commune</th>
                    <th className="hidden px-5 py-3 sm:table-cell">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lieux.map((l) => (
                    <tr key={l.id} className="border-b border-pblv-ink/5 last:border-0" data-testid={`admin-lieu-row-${l.id}`}>
                      <td className="px-5 py-3 font-medium text-pblv-ink">{l.name}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${l.type === "bien-etre" ? "bg-pblv-mauve/15 text-pblv-mauvedark" : "bg-pblv-sage/20 text-[#5f7052]"}`}>
                          {l.type === "hebergement" ? "Hébergement" : l.type === "bien-etre" ? "Bien-être" : "Héberg. & Bien-être"}
                        </span>
                      </td>
                      <td className="hidden px-5 py-3 text-pblv-inksoft md:table-cell">{l.commune}</td>
                      <td className="hidden px-5 py-3 sm:table-cell">
                        <span className={l.active ? "text-emerald-600" : "text-pblv-inksoft"}>
                          {l.active ? "Visible" : "Masqué"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            data-testid={`admin-edit-lieu-${l.id}`}
                            onClick={() => {
                              setEditing(l);
                              setDialogOpen(true);
                            }}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-pblv-ink transition-colors hover:bg-pblv-bg"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            data-testid={`admin-delete-lieu-${l.id}`}
                            onClick={() => deleteLieu(l.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {lieux.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-pblv-inksoft">
                        Aucun lieu pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Demandes de bon tab */}
        {tab === "demandes" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <button
                data-testid="admin-export-demandes"
                onClick={() => exportCsv("demandes")}
                disabled={demandes.length === 0}
                className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-pblv-ink transition-all hover:-translate-y-0.5 hover:bg-pblv-bg disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <Download className="h-4 w-4" /> Exporter en CSV
              </button>
            </div>
            <div className="space-y-3 md:hidden" data-testid="admin-demandes-cards">
              {demandes.map((d) => {
                const meta = {
                  nouveau: { label: "Nouveau", cls: "bg-pblv-rose/25 text-[#8a5a1e]" },
                  "validée": { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
                  validee: { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
                  "utilisée": { label: "Utilisée", cls: "bg-pblv-sage/25 text-[#5f7052]" },
                  utilisee: { label: "Utilisée", cls: "bg-pblv-sage/25 text-[#5f7052]" },
                  "traité": { label: "Traité", cls: "bg-emerald-100 text-emerald-700" },
                }[d.status] || { label: d.status, cls: "bg-pblv-ink/10 text-pblv-ink" };
                return (
                  <div key={d.id} data-testid={`admin-demande-card-${d.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-pblv-ink">{d.nom}</p>
                      <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-pblv-inksoft">
                      {d.eligible && <p className="text-xs text-emerald-600">✓ Éligibilité confirmée</p>}
                      {d.commune && <p className="text-xs">{d.commune}</p>}
                      <p className="text-xs">{d.email}{d.tel ? ` · ${d.tel}` : ""}</p>
                      {d.situation && <p className="text-xs italic">« {d.situation} »</p>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                      {d.status === "nouveau" && (
                        <button data-testid={`admin-validate-demande-m-${d.id}`} onClick={() => validateDemande(d.id)} className={`${mbtn} border-pblv-ink/10 text-emerald-600 hover:bg-emerald-50`}>
                          <Check className="h-4 w-4" /> Valider
                        </button>
                      )}
                      {(d.status === "validée" || d.status === "validee") && (
                        <button data-testid={`admin-use-demande-m-${d.id}`} onClick={() => setDemandeStatus(d.id, "utilisée")} className={`${mbtn} border-pblv-ink/10 text-[#5f7052] hover:bg-pblv-sage/10`}>
                          <Check className="h-4 w-4" /> Utilisée
                        </button>
                      )}
                      {d.status !== "nouveau" && (
                        <button data-testid={`admin-reset-demande-m-${d.id}`} onClick={() => setDemandeStatus(d.id, "nouveau")} className={`${mbtn} border-pblv-ink/10 text-pblv-inksoft hover:bg-pblv-bg`}>
                          Rouvrir
                        </button>
                      )}
                      <button data-testid={`admin-delete-demande-m-${d.id}`} onClick={() => deleteDemande(d.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                        <Trash2 className="h-4 w-4" /> Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
              {demandes.length === 0 && <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucune demande de bon pour le moment.</p>}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
            <table className="w-full text-left text-sm" data-testid="admin-demandes-table">
              <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                <tr>
                  <th className="px-5 py-3">Bénéficiaire</th>
                  <th className="hidden px-5 py-3 md:table-cell">Commune</th>
                  <th className="hidden px-5 py-3 lg:table-cell">Contact</th>
                  <th className="px-5 py-3">Statut</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {demandes.map((d) => (
                  <tr key={d.id} className="border-b border-pblv-ink/5 align-top last:border-0" data-testid={`admin-demande-row-${d.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-pblv-ink">{d.nom}</p>
                      {d.eligible && <p className="text-xs text-emerald-600">✓ Éligibilité confirmée</p>}
                      {d.situation && <p className="mt-1 max-w-xs text-xs italic text-pblv-inksoft">« {d.situation} »</p>}
                    </td>
                    <td className="hidden px-5 py-3 text-pblv-inksoft md:table-cell">{d.commune || "—"}</td>
                    <td className="hidden px-5 py-3 lg:table-cell">
                      <a href={`mailto:${d.email}`} className="text-pblv-mauve hover:underline">{d.email}</a>
                      {d.tel && <p className="text-xs text-pblv-inksoft">{d.tel}</p>}
                    </td>
                    <td className="px-5 py-3">
                      {(() => {
                        const meta = {
                          nouveau: { label: "Nouveau", cls: "bg-pblv-rose/25 text-[#8a5a1e]" },
                          "validée": { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
                          validee: { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
                          "utilisée": { label: "Utilisée", cls: "bg-pblv-sage/25 text-[#5f7052]" },
                          utilisee: { label: "Utilisée", cls: "bg-pblv-sage/25 text-[#5f7052]" },
                          "traité": { label: "Traité", cls: "bg-emerald-100 text-emerald-700" },
                        }[d.status] || { label: d.status, cls: "bg-pblv-ink/10 text-pblv-ink" };
                        return (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${meta.cls}`}>
                            {meta.label}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-1.5">
                        {d.status === "nouveau" && (
                          <button
                            data-testid={`admin-validate-demande-${d.id}`}
                            onClick={() => validateDemande(d.id)}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
                            title="Valider la demande et planifier"
                          >
                            <Check className="h-3.5 w-3.5" /> Valider
                          </button>
                        )}
                        {(d.status === "validée" || d.status === "validee") && (
                          <button
                            data-testid={`admin-use-demande-${d.id}`}
                            onClick={() => setDemandeStatus(d.id, "utilisée")}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-[#5f7052] transition-colors hover:bg-pblv-sage/10"
                            title="Marquer comme utilisée"
                          >
                            <Check className="h-3.5 w-3.5" /> Utilisée
                          </button>
                        )}
                        {d.status !== "nouveau" && (
                          <button
                            data-testid={`admin-reset-demande-${d.id}`}
                            onClick={() => setDemandeStatus(d.id, "nouveau")}
                            className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-inksoft transition-colors hover:bg-pblv-bg"
                            title="Rouvrir la demande"
                          >
                            Rouvrir
                          </button>
                        )}
                        <button
                          data-testid={`admin-delete-demande-${d.id}`}
                          onClick={() => deleteDemande(d.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {demandes.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-pblv-inksoft">
                      Aucune demande de bon pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* Codes T'POZ tab */}
        {tab === "codes" && (
          <div className="mt-6 space-y-6">
            <div className="rounded-2xl border border-pblv-ink/10 bg-white p-5" data-testid="admin-plafonds">
              <h3 className="font-serif text-lg text-pblv-ink">Plafonds partenaires — nombre maximum de T'POZ par mois</h3>
              <p className="mt-1 text-xs text-pblv-inksoft">
                Vide = pas de limite. Quand le plafond du mois est atteint, le bouton « Obtenir cette T'POZ » des
                expériences du partenaire devient inactif sur le site jusqu'au 1er du mois suivant.
              </p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {PARTENAIRES_LISTE.map((p) => (
                  <div key={p} className="flex items-center justify-between gap-2 rounded-xl bg-pblv-bg px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-pblv-ink">{p}</p>
                      <p className="text-[11px] text-pblv-inksoft">{plafondUsage[p] || 0} code{(plafondUsage[p] || 0) > 1 ? "s" : ""} délivré{(plafondUsage[p] || 0) > 1 ? "s" : ""} ce mois-ci</p>
                    </div>
                    <input
                      type="number"
                      min="0"
                      data-testid={`admin-plafond-${p.replace(/\W+/g, "-").toLowerCase()}`}
                      value={plafondVals[p] ?? ""}
                      onChange={(e) => setPlafondVals((v) => ({ ...v, [p]: e.target.value }))}
                      onBlur={() => savePlafond(p)}
                      placeholder="∞"
                      className="h-8 w-16 shrink-0 rounded-lg border border-pblv-ink/15 bg-white px-2 text-center text-sm"
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-pblv-ink/10 bg-white p-5" data-testid="admin-partenaires-codes">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-pblv-inksoft">Codes partenaires</h3>
                  <a
                    href="/partenaire"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="admin-lien-page-partenaire"
                    className="text-xs font-semibold text-pblv-mauve underline underline-offset-4"
                  >
                    Ouvrir la page de vérification partenaire →
                  </a>
                </div>
                <p className="mt-1 text-xs text-pblv-inksoft">
                  À transmettre à chaque partenaire : son code lui permet de vérifier les codes T'POZ et de confirmer les venues sur /partenaire. Le bouton QR télécharge un QR code à imprimer qui ouvre la page de vérification avec son code déjà rempli.
                </p>
                {partCodes.length === 0 ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="admin-part-codes-vide">
                    <span>Les codes partenaires n'ont pas pu être chargés.</span>
                    <button
                      onClick={refresh}
                      data-testid="admin-part-codes-retry"
                      className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                    >
                      <RefreshCw className="h-3 w-3" /> Réessayer
                    </button>
                  </div>
                ) : (
                <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {partCodes.map((pc2) => (
                    <div key={pc2.code} data-testid={`admin-part-code-${pc2.code}`} className="flex items-center justify-between gap-2 rounded-xl bg-pblv-bg px-3.5 py-2.5 text-sm">
                      <span className="min-w-0 truncate pr-1 font-medium text-pblv-ink">{pc2.nom}</span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <span className="font-bold text-pblv-mauve">{pc2.code}</span>
                        <button
                          onClick={() => copyPartCode(pc2.code)}
                          data-testid={`admin-part-copier-${pc2.code}`}
                          title="Copier le code"
                          className="inline-flex items-center rounded-full border border-pblv-ink/15 p-1.5 text-pblv-inksoft transition-colors hover:bg-pblv-ink/5 hover:text-pblv-ink"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() =>
                            downloadCsv(`/admin/partenaires-codes/${pc2.code}/qr`, `qr-partenaire-${pc2.code}.png`).catch(() =>
                              toast.error("Téléchargement impossible")
                            )
                          }
                          data-testid={`admin-part-qr-${pc2.code}`}
                          title="Télécharger le QR code de vérification de ce partenaire"
                          className="inline-flex items-center gap-1 rounded-full border border-pblv-mauve/40 px-2 py-1 text-[11px] font-semibold text-pblv-mauve transition-colors hover:bg-pblv-mauve/10"
                        >
                          <QrCode className="h-3.5 w-3.5" /> QR
                        </button>
                        <button
                          onClick={() =>
                            downloadCsv(`/admin/partenaires-codes/${pc2.code}/affichette.pdf`, `affichette-tpoz-${pc2.code}.pdf`).catch(() =>
                              toast.error("Téléchargement impossible")
                            )
                          }
                          data-testid={`admin-part-affichette-${pc2.code}`}
                          title="Télécharger l'affichette A5 à imprimer (QR + mode d'emploi)"
                          className="inline-flex items-center gap-1 rounded-full border border-pblv-mauve/40 px-2 py-1 text-[11px] font-semibold text-pblv-mauve transition-colors hover:bg-pblv-mauve/10"
                        >
                          <Printer className="h-3.5 w-3.5" /> Affichette
                        </button>
                        <button
                          onClick={() => regenPartCode(pc2)}
                          data-testid={`admin-part-regen-${pc2.code}`}
                          title="Régénérer le code (l'ancien code et son QR cesseront de fonctionner)"
                          className="inline-flex items-center rounded-full border border-amber-500/40 p-1.5 text-amber-600 transition-colors hover:bg-amber-50"
                        >
                          <RefreshCw className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
                )}
              </div>
            <div className="flex flex-wrap items-center gap-2" data-testid="admin-codes-filtres">
              {[["all", "Toutes"], ["nouveau", "À traiter"], ["validé", "Validée"], ["utilisé", "Venue confirmée"], ["refusé", "Refusée"]].map(([k, lbl]) => (
                <button
                  key={k}
                  data-testid={`admin-codes-filtre-${k}`}
                  onClick={() => setCodeFilter(k)}
                  className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                    codeFilter === k
                      ? "border-transparent bg-pblv-mauve text-white"
                      : "border-pblv-ink/15 bg-white text-pblv-ink hover:border-pblv-mauve/50"
                  }`}
                >
                  {lbl}
                  {k === "nouveau" && stats?.a_traiter ? ` (${stats.a_traiter})` : ""}
                </button>
              ))}
              <button
                onClick={exportCodes}
                data-testid="admin-export-codes"
                className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-lg border border-pblv-ink/10 bg-white px-3 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-bg"
              >
                Exporter en CSV
              </button>
            </div>
            <div className="space-y-3 md:hidden" data-testid="admin-codes-cards">
              {codeDemandes.filter((d) => codeFilter === "all" || d.status === codeFilter).map((d) => {
                const meta = {
                  nouveau: { label: "Reçue", cls: "bg-pblv-rose/25 text-[#8a5a1e]" },
                  "validé": { label: "Validée", cls: "bg-emerald-100 text-emerald-700" },
                  "utilisé": { label: "Venue confirmée", cls: "bg-emerald-100 text-emerald-700" },
                  "refusé": { label: "Refusée", cls: "bg-red-100 text-red-600" },
                  "annulé": { label: "Code annulé", cls: "bg-pblv-ink/10 text-pblv-inksoft" },
                }[d.status] || { label: d.status, cls: "bg-pblv-ink/10 text-pblv-ink" };
                return (
                  <div key={d.id} data-testid={`admin-code-card-${d.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-pblv-ink">{d.prenom} {d.nom}</p>
                        {d.cadeau_type && (
                          <p className="mt-0.5 text-[11px] font-semibold text-pblv-mauve">
                            🎁 {d.cadeau_type === "proche" ? "Offerte à un proche" : d.cadeau_type === "heure_suspendue" ? "Heure suspendue" : "Groupe de femmes"}{d.offreur_nom ? ` · par ${d.offreur_nom}` : ""}
                          </p>
                        )}
                      </div>
                      <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${meta.cls}`}>{meta.label}</span>
                    </div>
                    <div className="mt-3 space-y-1">
                      {d.offre && <p className="text-sm font-semibold text-pblv-mauve">{d.offre}</p>}
                      <p className="text-xs text-pblv-inksoft">Reçue le {(d.created_at || "").slice(0, 10)}{d.tel ? ` · ${d.tel}` : ""}</p>
                      {d.code && (
                        <p className={`text-base font-bold tracking-wide ${d.status === "annulé" ? "text-pblv-inksoft line-through" : "text-pblv-mauve"}`}>{d.code}</p>
                      )}
                      {d.status === "validé" && d.valid_until && (
                        <p className="text-xs text-pblv-inksoft">Valable jusqu'au {new Date(d.valid_until).toLocaleDateString("fr-FR")}</p>
                      )}
                      {d.status === "utilisé" && (
                        <p className="text-xs font-semibold text-emerald-700">Venue confirmée{d.venue_confirmee_at ? ` le ${new Date(d.venue_confirmee_at).toLocaleDateString("fr-FR")}` : ""}{d.partenaire ? ` chez ${d.partenaire}` : ""}</p>
                      )}
                      {d.status === "validé" && (d.code_email_sent_at ? (
                        <p className="text-xs font-semibold text-emerald-700">Code envoyé le {new Date(d.code_email_sent_at).toLocaleDateString("fr-FR")}</p>
                      ) : (
                        <p className="text-xs font-semibold text-amber-700">Validée — mail non envoyé</p>
                      ))}
                      {d.cadeau_type === "heure_suspendue" && d.attribue_at && (
                        <p className="text-xs font-semibold text-pblv-mauve">Attribuée à {d.attribuee_prenom} {d.attribuee_nom} le {new Date(d.attribue_at).toLocaleDateString("fr-FR")}</p>
                      )}
                      {(d.codes_par_mail ?? 0) >= 3 && (
                        <p className="text-xs font-semibold text-red-600">Limite 3/3 atteinte{d.codes_reprise ? ` — reprise le ${d.codes_reprise}` : ""}</p>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                      {d.status === "nouveau" && (
                        <>
                          <button
                            data-testid={`admin-validate-code-m-${d.id}`}
                            onClick={() => validerCode(d.id)}
                            disabled={(d.codes_par_mail ?? 0) >= 3}
                            className={`${mbtn} border-pblv-ink/10 text-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40`}
                          >
                            <Check className="h-4 w-4" /> Valider
                          </button>
                          <button data-testid={`admin-refuser-code-m-${d.id}`} onClick={() => refuserCode(d.id)} className={`${mbtn} border-pblv-ink/10 text-red-500 hover:bg-red-50`}>
                            Refuser
                          </button>
                        </>
                      )}
                      {d.status === "validé" && (
                        <>
                          <button
                            data-testid={`admin-copy-code-m-${d.id}`}
                            onClick={() => navigator.clipboard.writeText(d.code).then(() => toast.success("Code copié", { description: d.code })).catch(() => window.prompt("Copiez le code :", d.code))}
                            className={`${mbtn} border-pblv-mauve/40 text-pblv-mauve hover:bg-pblv-mauve/10`}
                          >
                            <Copy className="h-4 w-4" /> Copier le code
                          </button>
                          <button data-testid={`admin-renvoyer-code-m-${d.id}`} onClick={() => renvoyerCode(d.id)} className={`${mbtn} border-pblv-ink/10 text-pblv-mauve hover:bg-pblv-bg`}>
                            Renvoyer le code
                          </button>
                          {d.cadeau_type && d.code && (
                            <button
                              data-testid={`admin-bon-pdf-m-${d.id}`}
                              onClick={() => downloadCsv(`/admin/code-demandes/${d.id}/bon.pdf`, `bon-tpoz-${d.code}.pdf`).catch(() => toast.error("Téléchargement impossible"))}
                              className={`${mbtn} border-pblv-mauve/40 text-pblv-mauve hover:bg-pblv-mauve/10`}
                            >
                              <Download className="h-4 w-4" /> Bon PDF
                            </button>
                          )}
                          {d.cadeau_type === "heure_suspendue" && !d.attribue_at && (
                            <button data-testid={`admin-attribuer-m-${d.id}`} onClick={() => { setAttribForm(EMPTY_ATTRIB); setAttribFor(d); }} className={`${mbtn} border-transparent bg-pblv-mauve text-white hover:opacity-90`}>
                              <HeartHandshake className="h-4 w-4" /> Attribuer
                            </button>
                          )}
                          <button data-testid={`admin-annuler-code-m-${d.id}`} onClick={() => annulerCode(d.id)} className={`${mbtn} border-pblv-ink/10 text-orange-600 hover:bg-orange-50`}>
                            Annuler
                          </button>
                        </>
                      )}
                      <button data-testid={`admin-delete-code-m-${d.id}`} onClick={() => deleteCodeDemande(d.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                        <Trash2 className="h-4 w-4" /> Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
              {codeDemandes.filter((d) => codeFilter === "all" || d.status === codeFilter).length === 0 && (
                <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucune demande de code pour le moment.</p>
              )}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
              <table className="w-full text-left text-sm" data-testid="admin-codes-table">
                <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                  <tr>
                    <th className="px-5 py-3">Bénéficiaire</th>
                    <th className="hidden px-5 py-3 md:table-cell">Demande</th>
                    <th className="px-5 py-3">Utilisations sur 12 mois</th>
                    <th className="px-5 py-3">Code / Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {codeDemandes.filter((d) => codeFilter === "all" || d.status === codeFilter).map((d) => (
                    <tr key={d.id} className="border-b border-pblv-ink/5 align-top last:border-0" data-testid={`admin-code-row-${d.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-pblv-ink">{d.prenom} {d.nom}</p>
                        {d.cadeau_type && (
                          <p data-testid={`admin-code-cadeau-${d.id}`} className="mt-0.5 inline-flex items-center gap-1 rounded-full bg-pblv-mauve/10 px-2 py-0.5 text-[11px] font-semibold text-pblv-mauve">
                            🎁 {d.cadeau_type === "proche" ? "Offerte à un proche" : d.cadeau_type === "heure_suspendue" ? "Heure suspendue" : "Groupe de femmes"}
                            {d.offreur_nom ? ` · par ${d.offreur_nom}` : ""}
                          </p>
                        )}
                        <p className="text-xs text-pblv-inksoft">{d.commune}{d.age ? ` · ${d.age === "40+" ? "40 ans et plus" : "Moins de 40 ans"}` : ""}</p>
                        <p className="text-xs text-pblv-inksoft">{d.tel}{d.email ? ` · ${d.email}` : ""}</p>
                        {d.structure_nom && (
                          <p className="text-xs text-pblv-mauve">Demande faite par : {d.structure_nom}</p>
                        )}
                      </td>
                      <td className="hidden px-5 py-3 md:table-cell">
                        {d.offre && <p className="text-xs font-semibold text-pblv-mauve">{d.offre}</p>}
                        {d.besoin && <p className="text-xs text-pblv-inksoft">Besoin : {d.besoin}</p>}
                        <p className="text-xs text-pblv-inksoft">Reçue le {(d.created_at || "").slice(0, 10)}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span
                          data-testid={`admin-codes-12m-${d.id}`}
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ${
                            (d.codes_par_mail ?? 0) >= 3
                              ? "bg-red-100 text-red-600"
                              : "bg-pblv-ink/8 text-pblv-ink"
                          }`}
                        >
                          {Math.min(d.codes_par_mail ?? 0, 3)}/3
                        </span>
                        {(d.codes_par_mail ?? 0) >= 3 && (
                          <p data-testid={`admin-codes-limite-${d.id}`} className="mt-1.5 max-w-[160px] text-[11px] font-semibold leading-snug text-red-600">
                            Limite atteinte{d.codes_reprise ? ` — nouveau code possible à partir du ${d.codes_reprise}` : ""}
                          </p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {d.status === "refusé" ? (
                          <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-600">Refusée</span>
                        ) : d.status === "utilisé" ? (
                          <div>
                            <p className="text-sm font-bold text-pblv-mauve">{d.code}</p>
                            <span data-testid={`admin-code-venue-${d.id}`} className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                              Venue confirmée{d.venue_confirmee_at ? ` le ${new Date(d.venue_confirmee_at).toLocaleDateString("fr-FR")}` : ""}
                            </span>
                            {d.partenaire && <p className="mt-1 text-[11px] text-pblv-inksoft">chez {d.partenaire}</p>}
                          </div>
                        ) : d.status === "annulé" ? (
                          <div>
                            <p className="text-sm font-bold text-pblv-inksoft line-through">{d.code}</p>
                            <span className="inline-flex items-center rounded-full bg-pblv-ink/10 px-2.5 py-1 text-xs font-semibold text-pblv-inksoft">Code annulé</span>
                          </div>
                        ) : d.code ? (
                          <div>
                            <p className="text-sm font-bold text-pblv-mauve">{d.code}</p>
                            {d.valid_until && (
                              <p data-testid={`admin-code-validite-${d.id}`} className="text-[11px] text-pblv-inksoft">
                                Valable jusqu'au {new Date(d.valid_until).toLocaleDateString("fr-FR")}
                              </p>
                            )}
                            {d.cadeau_type && (
                              <button
                                onClick={() => downloadCsv(`/admin/code-demandes/${d.id}/bon.pdf`, `bon-tpoz-${d.code}.pdf`).catch(() => toast.error("Téléchargement impossible"))}
                                data-testid={`admin-bon-pdf-${d.id}`}
                                className="mt-1 inline-flex items-center gap-1 rounded-full border border-pblv-mauve/40 px-2 py-0.5 text-[11px] font-semibold text-pblv-mauve hover:bg-pblv-mauve/10"
                              >
                                <Download className="h-3 w-3" /> Bon PDF
                              </button>
                            )}
                            {d.cadeau_type === "heure_suspendue" && d.status === "validé" && (
                              d.attribue_at ? (
                                <span data-testid={`admin-attribuee-${d.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-pblv-mauve/10 px-2.5 py-1 text-[11px] font-semibold text-pblv-mauve">
                                  <HeartHandshake className="h-3 w-3" /> Attribuée à {d.attribuee_prenom} {d.attribuee_nom} le {new Date(d.attribue_at).toLocaleDateString("fr-FR")}
                                </span>
                              ) : (
                                <button
                                  onClick={() => { setAttribForm(EMPTY_ATTRIB); setAttribFor(d); }}
                                  data-testid={`admin-attribuer-${d.id}`}
                                  className="mt-1 inline-flex items-center gap-1 rounded-full bg-pblv-mauve px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90"
                                >
                                  <HeartHandshake className="h-3 w-3" /> Attribuer
                                </button>
                              )
                            )}
                            {d.code_email_sent_at ? (
                              <span data-testid={`admin-code-envoye-${d.id}`} className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                                Code envoyé le {new Date(d.code_email_sent_at).toLocaleDateString("fr-FR")}
                              </span>
                            ) : (
                              <span data-testid={`admin-code-nonenvoye-${d.id}`} className="mt-1 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-700">
                                Validée — mail non envoyé
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-pblv-rose/25 px-2.5 py-1 text-xs font-semibold text-[#8a5a1e]">Reçue</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1.5">
                          {d.status === "validé" ? (
                            <>
                              <button
                                data-testid={`admin-renvoyer-code-${d.id}`}
                                onClick={() => renvoyerCode(d.id)}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-bg"
                              >
                                Renvoyer le code
                              </button>
                              <button
                                data-testid={`admin-copy-sms-${d.id}`}
                                onClick={() => copySms(d)}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-bg"
                              >
                                Copier le message
                              </button>
                              <button
                                data-testid={`admin-annuler-code-${d.id}`}
                                onClick={() => annulerCode(d.id)}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-orange-600 transition-colors hover:bg-orange-50"
                              >
                                Annuler
                              </button>
                            </>
                          ) : d.status === "refusé" || d.status === "annulé" ? null : (
                            <>
                              <button
                                data-testid={`admin-validate-code-${d.id}`}
                                onClick={() => validerCode(d.id)}
                                disabled={(d.codes_par_mail ?? 0) >= 3}
                                title={(d.codes_par_mail ?? 0) >= 3 ? `Limite de 3 T'POZ sur 12 mois atteinte${d.codes_reprise ? ` — reprise le ${d.codes_reprise}` : ""}` : undefined}
                                className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                              >
                                <Check className="h-3.5 w-3.5" /> Valider
                              </button>
                              <button
                                data-testid={`admin-refuser-code-${d.id}`}
                                onClick={() => refuserCode(d.id)}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-red-500 transition-colors hover:bg-red-50"
                              >
                                Refuser
                              </button>
                            </>
                          )}
                          <button
                            data-testid={`admin-delete-code-${d.id}`}
                            onClick={() => deleteCodeDemande(d.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {codeDemandes.filter((d) => codeFilter === "all" || d.status === codeFilter).length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-pblv-inksoft">
                        Aucune demande de code pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Éligibilité tab */}
        {tab === "acces" && (
          <div className="mt-6" data-testid="admin-acces-list">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-pblv-inksoft">
                {accesList.length} inscription{accesList.length > 1 ? "s" : ""} enregistrée{accesList.length > 1 ? "s" : ""}
              </p>
              <button
                onClick={exportAcces}
                data-testid="admin-export-acces"
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-pblv-ink/10 px-3 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-white"
              >
                Exporter en CSV
              </button>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              {accesList.map((a) => (
                <div
                  key={a.id || a.email}
                  data-testid={`admin-acces-${a.id || a.email}`}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 rounded-2xl bg-white px-5 py-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-pblv-ink">{a.prenom || "—"}</p>
                    <p className="text-xs text-pblv-inksoft">{a.email}</p>
                    {a.experience_premiere && (
                      <p className="text-xs text-pblv-inksoft">Première expérience : {a.experience_premiere}</p>
                    )}
                    {a.experience_derniere && a.experience_derniere !== a.experience_premiere && (
                      <p className="text-xs text-pblv-inksoft">Dernière expérience : {a.experience_derniere}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-semibold ${a.consent ? "text-emerald-600" : "text-pblv-inksoft"}`}>
                      Nouvelles T'POZ : {a.consent ? "Oui" : "Non"}
                    </p>
                    <p className="text-xs text-pblv-inksoft">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : ""}
                    </p>
                  </div>
                </div>
              ))}
              {accesList.length === 0 && (
                <p className="text-sm text-pblv-inksoft">Aucun accès pour le moment.</p>
              )}
            </div>
          </div>
        )}

        {tab === "gestionpart" && (() => {
          const normName = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
          const offresByPart = {};
          EXP_DATA.forEach((exp) =>
            (exp.offres || []).forEach((o) => {
              const k = normName(o.partenaire);
              if (!offresByPart[k]) offresByPart[k] = { offres: [], tel: "" };
              offresByPart[k].offres.push(`${exp.nom} — ${o.lieu || ""}`);
              if (!offresByPart[k].tel && o.tel) offresByPart[k].tel = o.tel;
            })
          );
          const rows = partCodes
            .map((pc2) => {
              const info = offresByPart[normName(pc2.nom)] || { offres: [], tel: "" };
              return { ...pc2, offres: info.offres, contact: info.tel, statut: info.offres.length ? "Actif" : "Sans offre" };
            })
            .filter((r) => {
              const q = partSearch.trim().toLowerCase();
              return !q || r.nom.toLowerCase().includes(q) || r.code.toLowerCase().includes(q);
            });
          const StatutBadge = ({ s }) => (
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${s === "Actif" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-ink/8 text-pblv-inksoft"}`}>{s}</span>
          );
          return (
            <div className="mt-6 space-y-4" data-testid="admin-gestionpart-panel">
              <input
                value={partSearch}
                onChange={(e) => setPartSearch(e.target.value)}
                placeholder="Rechercher par code ou par nom…"
                data-testid="gestionpart-recherche"
                className="w-full rounded-full border border-pblv-ink/15 bg-white px-5 py-3 text-sm outline-none focus:border-pblv-mauve focus:ring-2 focus:ring-pblv-mauve/20 sm:max-w-md"
              />
              {/* Tableau ordinateur */}
              <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
                <table className="w-full text-left text-sm" data-testid="gestionpart-table">
                  <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                    <tr>
                      <th className="px-5 py-3">Code partenaire</th>
                      <th className="px-5 py-3">Partenaire</th>
                      <th className="px-5 py-3">Contact</th>
                      <th className="px-5 py-3">Offres rattachées</th>
                      <th className="px-5 py-3">Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.code} onClick={() => setPartFiche(r)} data-testid={`gestionpart-row-${r.code}`} className="cursor-pointer border-b border-pblv-ink/5 last:border-0 hover:bg-pblv-bg/60">
                        <td className="px-5 py-3 font-bold text-pblv-mauve">{r.code}</td>
                        <td className="px-5 py-3 font-medium">{r.nom}</td>
                        <td className="px-5 py-3 text-pblv-inksoft">{r.contact || "—"}</td>
                        <td className="px-5 py-3">{r.offres.length}</td>
                        <td className="px-5 py-3"><StatutBadge s={r.statut} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Cartes téléphone */}
              <div className="space-y-3 md:hidden">
                {rows.map((r) => (
                  <div key={r.code} onClick={() => setPartFiche(r)} data-testid={`gestionpart-card-${r.code}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-bold text-pblv-mauve">{r.code}</p>
                      <button
                        onClick={(e) => { e.stopPropagation(); copyPartCode(r.code); }}
                        data-testid={`gestionpart-copier-${r.code}`}
                        className="inline-flex items-center gap-1 rounded-full border border-pblv-ink/15 px-2.5 py-1 text-[11px] font-semibold text-pblv-inksoft"
                      >
                        <Copy className="h-3 w-3" /> Copier
                      </button>
                    </div>
                    <p className="mt-1 font-medium">{r.nom}</p>
                    <p className="text-xs text-pblv-inksoft">{r.contact || "—"} · {r.offres.length} offre{r.offres.length > 1 ? "s" : ""}</p>
                    <div className="mt-2"><StatutBadge s={r.statut} /></div>
                  </div>
                ))}
              </div>
              {rows.length === 0 && (
                <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucun partenaire ne correspond à cette recherche.</p>
              )}
              {/* Fiche partenaire */}
              <Dialog open={Boolean(partFiche)} onOpenChange={(v) => !v && setPartFiche(null)}>
                <DialogContent aria-describedby={undefined} data-testid="gestionpart-fiche" className="max-w-md">
                  {partFiche && (
                    <>
                      <DialogHeader>
                        <DialogTitle className="font-serif text-2xl font-normal">{partFiche.nom}</DialogTitle>
                      </DialogHeader>
                      <div className="flex items-center justify-between rounded-xl bg-pblv-bg px-4 py-3">
                        <p className="text-lg font-bold text-pblv-mauve" data-testid="gestionpart-fiche-code">{partFiche.code}</p>
                        <button
                          onClick={() => copyPartCode(partFiche.code)}
                          className="inline-flex items-center gap-1 rounded-full border border-pblv-ink/15 px-2.5 py-1 text-xs font-semibold text-pblv-inksoft"
                        >
                          <Copy className="h-3 w-3" /> Copier
                        </button>
                      </div>
                      <p className="text-xs text-pblv-inksoft">Le code partenaire ne peut pas être modifié. Utilisez « Régénérer » dans l'onglet Codes T'POZ si nécessaire.</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() =>
                            downloadCsv(`/admin/partenaires-codes/${partFiche.code}/affichette.pdf`, `affichette-tpoz-${partFiche.code}.pdf`).catch(() =>
                              toast.error("Téléchargement impossible")
                            )
                          }
                          data-testid="gestionpart-fiche-affichette"
                          className="inline-flex items-center gap-1.5 rounded-full border border-pblv-mauve/40 px-3 py-1.5 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-mauve/10"
                        >
                          <Printer className="h-3.5 w-3.5" /> Affichette A5
                        </button>
                        <button
                          onClick={() =>
                            downloadCsv(`/admin/partenaires-codes/${partFiche.code}/qr`, `qr-partenaire-${partFiche.code}.png`).catch(() =>
                              toast.error("Téléchargement impossible")
                            )
                          }
                          data-testid="gestionpart-fiche-qr"
                          className="inline-flex items-center gap-1.5 rounded-full border border-pblv-ink/15 px-3 py-1.5 text-xs font-semibold text-pblv-inksoft transition-colors hover:bg-pblv-ink/5"
                        >
                          <QrCode className="h-3.5 w-3.5" /> QR seul
                        </button>
                      </div>
                      <p className="mt-2 text-sm font-bold">Offres rattachées ({partFiche.offres.length})</p>
                      <div className="max-h-56 space-y-1.5 overflow-y-auto">
                        {partFiche.offres.map((o, i) => (
                          <p key={i} className="rounded-lg bg-pblv-bg px-3 py-2 text-sm">{o}</p>
                        ))}
                        {partFiche.offres.length === 0 && <p className="text-sm text-pblv-inksoft">Aucune offre rattachée pour le moment.</p>}
                      </div>
                    </>
                  )}
                </DialogContent>
              </Dialog>
            </div>
          );
        })()}

        {tab === "messages" && (
          <div className="mt-6 space-y-3" data-testid="admin-messages-panel">
            {messages.length === 0 && (
              <p className="rounded-2xl border border-pblv-ink/10 bg-white p-6 text-sm text-pblv-inksoft">
                Aucun message reçu via les formulaires du site pour le moment.
              </p>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                data-testid={`admin-message-${msg.id}`}
                className={`rounded-2xl border bg-white p-5 ${msg.traite ? "border-pblv-ink/8 opacity-70" : "border-pblv-mauve/30"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-pblv-ink">{msg.sujet}</p>
                    <p className="mt-0.5 text-xs text-pblv-inksoft">
                      {msg.nom} · <a href={`mailto:${msg.email}`} className="text-pblv-mauve underline underline-offset-2">{msg.email}</a>
                      {msg.tel ? ` · ${msg.tel}` : ""} · {new Date(msg.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                    </p>
                  </div>
                  <button
                    onClick={() => toggleMessageTraite(msg)}
                    data-testid={`admin-message-traiter-${msg.id}`}
                    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
                      msg.traite
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : "border border-pblv-mauve/40 text-pblv-mauve hover:bg-pblv-mauve/10"
                    }`}
                  >
                    {msg.traite ? "✓ Traité" : "Marquer traité"}
                  </button>
                </div>
                <p className="mt-3 whitespace-pre-wrap rounded-xl bg-pblv-bg px-4 py-3 text-sm text-pblv-ink">{msg.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "eligibilite" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <button
                data-testid="admin-export-eligibilites"
                onClick={() => exportCsv("eligibilites")}
                disabled={eligibilites.length === 0}
                className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-pblv-ink transition-all hover:-translate-y-0.5 hover:bg-pblv-bg disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <Download className="h-4 w-4" /> Exporter en CSV
              </button>
            </div>
            <div className="space-y-3 md:hidden" data-testid="admin-eligibilites-cards">
              {eligibilites.map((e) => (
                <div key={e.id} data-testid={`admin-elig-card-${e.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-pblv-ink">{e.nom} {e.prenom}</p>
                    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${e.status === "validé" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#8a5a1e]"}`}>
                      {e.status === "validé" ? "Validé" : "Nouveau"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-pblv-inksoft">
                    <p>{e.annee_naissance} · {e.commune}</p>
                    <p>{e.tel}{e.email ? ` · ${e.email}` : ""}</p>
                    {e.oriente === "oui" && <p>Orientée par : {e.structure || "structure non précisée"}</p>}
                    <p>Bons cette année : {e.bons_annee === "non" ? "Non" : `${e.bons_annee} fois`}</p>
                    {e.dossier && <p className="font-semibold text-pblv-mauve">Dossier : {e.dossier}</p>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                    {e.status !== "validé" ? (
                      <button data-testid={`admin-validate-elig-m-${e.id}`} onClick={() => validerElig(e.id)} className={`${mbtn} border-pblv-ink/10 text-emerald-600 hover:bg-emerald-50`}>
                        <Check className="h-4 w-4" /> Valider
                      </button>
                    ) : (
                      <>
                        <button data-testid={`admin-copy-link-m-${e.id}`} onClick={() => copyBonLink(e.dossier)} className={`${mbtn} border-pblv-mauve/40 text-pblv-mauve hover:bg-pblv-mauve/10`}>
                          <Copy className="h-4 w-4" /> Copier le lien
                        </button>
                        <button data-testid={`admin-reopen-elig-m-${e.id}`} onClick={() => setEligStatus(e.id, "nouveau")} className={`${mbtn} border-pblv-ink/10 text-pblv-inksoft hover:bg-pblv-bg`}>
                          Rouvrir
                        </button>
                      </>
                    )}
                    <button data-testid={`admin-delete-elig-m-${e.id}`} onClick={() => deleteElig(e.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {eligibilites.length === 0 && <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucune demande d'éligibilité pour le moment.</p>}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
              <table className="w-full text-left text-sm" data-testid="admin-eligibilites-table">
                <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                  <tr>
                    <th className="px-5 py-3">Bénéficiaire</th>
                    <th className="hidden px-5 py-3 md:table-cell">Contact</th>
                    <th className="hidden px-5 py-3 lg:table-cell">Situation</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {eligibilites.map((e) => (
                    <tr key={e.id} className="border-b border-pblv-ink/5 align-top last:border-0" data-testid={`admin-elig-row-${e.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-pblv-ink">{e.nom} {e.prenom}</p>
                        <p className="text-xs text-pblv-inksoft">{e.annee_naissance} · {e.commune}</p>
                        {e.oriente === "oui" && (
                          <p className="text-xs text-pblv-inksoft">Orientée par : {e.structure || "structure non précisée"}</p>
                        )}
                        <p className="text-xs text-pblv-inksoft">Bons cette année : {e.bons_annee === "non" ? "Non" : `${e.bons_annee} fois`}</p>
                        {e.dossier && (
                          <p className="mt-1 text-xs font-semibold text-pblv-mauve">Dossier : {e.dossier}</p>
                        )}
                      </td>
                      <td className="hidden px-5 py-3 md:table-cell">
                        <p className="text-pblv-ink">{e.tel}</p>
                        {e.email && <a href={`mailto:${e.email}`} className="text-xs text-pblv-mauve hover:underline">{e.email}</a>}
                        <p className="text-xs text-pblv-inksoft">
                          Préfère : {e.contact_pref === "telephone" ? "téléphone" : "e-mail"}
                          {e.contact_moment ? ` · ${e.contact_moment === "apres-midi" ? "après-midi" : e.contact_moment}` : ""}
                        </p>
                      </td>
                      <td className="hidden px-5 py-3 lg:table-cell">
                        <ul className="text-xs text-pblv-inksoft">
                          {(e.situations || []).map((s) => <li key={s}>• {s}</li>)}
                        </ul>
                        {e.message && <p className="mt-1 max-w-xs text-xs italic text-pblv-inksoft">« {e.message} »</p>}
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${e.status === "validé" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#8a5a1e]"}`}>
                          {e.status === "validé" ? "Validé" : "Nouveau"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1.5">
                          {e.status !== "validé" ? (
                            <button
                              data-testid={`admin-validate-elig-${e.id}`}
                              onClick={() => validerElig(e.id)}
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
                            >
                              <Check className="h-3.5 w-3.5" /> Valider
                            </button>
                          ) : (
                            <>
                              <button
                                data-testid={`admin-copy-link-${e.id}`}
                                onClick={() => copyBonLink(e.dossier)}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-bg"
                              >
                                Copier le lien
                              </button>
                              <button
                                data-testid={`admin-reopen-elig-${e.id}`}
                                onClick={() => setEligStatus(e.id, "nouveau")}
                                className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-inksoft transition-colors hover:bg-pblv-bg"
                              >
                                Rouvrir
                              </button>
                            </>
                          )}
                          <button
                            data-testid={`admin-delete-elig-${e.id}`}
                            onClick={() => deleteElig(e.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {eligibilites.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-pblv-inksoft">
                        Aucune demande d'éligibilité pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bons tab */}
        {tab === "bons" && (
          <div className="mt-6">
            <div className="space-y-3 md:hidden" data-testid="admin-bons-cards">
              {bons.map((b) => (
                <div key={b.id} data-testid={`admin-bon-card-${b.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-pblv-ink">{b.nom_prenom}</p>
                    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${b.status === "bon_envoye" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#8a5a1e]"}`}>
                      {b.status === "bon_envoye" ? "Bon envoyé" : "À traiter"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-pblv-inksoft">
                    <p className="font-semibold text-pblv-mauve">Dossier : {b.numero_dossier}</p>
                    <p className="text-sm text-pblv-ink">{b.partenaire}</p>
                    <p>{b.experience}</p>
                    <p>Du {b.date_debut} au {b.date_fin}</p>
                    <p>Reçue le {(b.created_at || "").slice(0, 10)}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                    <button data-testid={`admin-bon-pdf-m-${b.id}`} onClick={() => openBonPdf(b)} className={`${mbtn} border-pblv-mauve/40 text-pblv-mauve hover:bg-pblv-mauve/10`}>
                      <Download className="h-4 w-4" /> Bon PDF
                    </button>
                    {b.status !== "bon_envoye" ? (
                      <button data-testid={`admin-send-bon-m-${b.id}`} onClick={() => setBonStatus(b.id, "bon_envoye")} className={`${mbtn} border-pblv-ink/10 text-emerald-600 hover:bg-emerald-50`}>
                        <Check className="h-4 w-4" /> Bon envoyé
                      </button>
                    ) : (
                      <button data-testid={`admin-reopen-bon-m-${b.id}`} onClick={() => setBonStatus(b.id, "a_traiter")} className={`${mbtn} border-pblv-ink/10 text-pblv-inksoft hover:bg-pblv-bg`}>
                        Rouvrir
                      </button>
                    )}
                    <button data-testid={`admin-delete-bon-m-${b.id}`} onClick={() => deleteBon(b.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {bons.length === 0 && <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucune demande de bon pour le moment.</p>}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
              <table className="w-full text-left text-sm" data-testid="admin-bons-table">
                <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                  <tr>
                    <th className="px-5 py-3">Bénéficiaire</th>
                    <th className="hidden px-5 py-3 md:table-cell">Réservation</th>
                    <th className="px-5 py-3">Statut</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bons.map((b) => (
                    <tr key={b.id} className="border-b border-pblv-ink/5 align-top last:border-0" data-testid={`admin-bon-row-${b.id}`}>
                      <td className="px-5 py-3">
                        <p className="font-medium text-pblv-ink">{b.nom_prenom}</p>
                        <p className="text-xs font-semibold text-pblv-mauve">Dossier : {b.numero_dossier}</p>
                        <p className="text-xs text-pblv-inksoft">Reçue le {(b.created_at || "").slice(0, 10)}</p>
                      </td>
                      <td className="hidden px-5 py-3 md:table-cell">
                        <p className="text-pblv-ink">{b.partenaire}</p>
                        <p className="text-xs text-pblv-inksoft">{b.experience}</p>
                        <p className="text-xs text-pblv-inksoft">Du {b.date_debut} au {b.date_fin}</p>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${b.status === "bon_envoye" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#8a5a1e]"}`}>
                          {b.status === "bon_envoye" ? "Bon envoyé" : "À traiter"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1.5">
                          <button
                            data-testid={`admin-bon-pdf-${b.id}`}
                            onClick={() => openBonPdf(b)}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-mauve transition-colors hover:bg-pblv-bg"
                          >
                            <Download className="h-3.5 w-3.5" /> Bon PDF
                          </button>
                          {b.status !== "bon_envoye" ? (
                            <button
                              data-testid={`admin-send-bon-${b.id}`}
                              onClick={() => setBonStatus(b.id, "bon_envoye")}
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
                            >
                              <Check className="h-3.5 w-3.5" /> Bon envoyé
                            </button>
                          ) : (
                            <button
                              data-testid={`admin-reopen-bon-${b.id}`}
                              onClick={() => setBonStatus(b.id, "a_traiter")}
                              className="inline-flex h-8 items-center rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-pblv-inksoft transition-colors hover:bg-pblv-bg"
                            >
                              Rouvrir
                            </button>
                          )}
                          <button
                            data-testid={`admin-delete-bon-${b.id}`}
                            onClick={() => deleteBon(b.id)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {bons.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-5 py-10 text-center text-pblv-inksoft">
                        Aucune demande de bon pour le moment.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Attribution heure suspendue dialog */}
        <Dialog open={Boolean(attribFor)} onOpenChange={(v) => !v && setAttribFor(null)}>
          <DialogContent aria-describedby={undefined} data-testid="attribution-dialog" className="rounded-3xl bg-pblv-bg sm:max-w-md">
            {attribFor && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl font-normal">Attribuer l'heure suspendue</DialogTitle>
                </DialogHeader>
                <form onSubmit={submitAttribution} className="space-y-4">
                  <p className="rounded-xl bg-white px-4 py-3 text-sm text-pblv-inksoft">
                    Code <span className="font-bold text-pblv-mauve">{attribFor.code}</span> · {attribFor.offre}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-pblv-inksoft">Prénom *</label>
                      <input data-testid="attrib-prenom" required value={attribForm.prenom} onChange={(e) => setAttribForm((f) => ({ ...f, prenom: e.target.value }))} className={field} />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-pblv-inksoft">Nom</label>
                      <input data-testid="attrib-nom" value={attribForm.nom} onChange={(e) => setAttribForm((f) => ({ ...f, nom: e.target.value }))} className={field} />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-pblv-inksoft">E-mail</label>
                    <input data-testid="attrib-email" type="email" value={attribForm.email} onChange={(e) => setAttribForm((f) => ({ ...f, email: e.target.value }))} className={field} />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-pblv-inksoft">Téléphone</label>
                    <input data-testid="attrib-tel" type="tel" value={attribForm.tel} onChange={(e) => setAttribForm((f) => ({ ...f, tel: e.target.value }))} className={field} placeholder="0690 ..." />
                  </div>
                  <p className="text-xs text-pblv-inksoft">
                    La bénéficiaire recevra le code T'POZ par e-mail (avec le bon PDF joint) et/ou par SMS. Indiquez au moins un e-mail ou un téléphone.
                  </p>
                  <button
                    type="submit"
                    disabled={attribLoading}
                    data-testid="attrib-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                  >
                    {attribLoading ? "Attribution…" : "Attribuer et envoyer le bon"}
                  </button>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Bon PDF dialog */}
        <Dialog open={!!bonPdfFor} onOpenChange={(o) => !o && setBonPdfFor(null)}>
          <DialogContent aria-describedby={undefined} data-testid="bon-pdf-dialog" className="max-h-[90vh] overflow-y-auto rounded-3xl bg-pblv-bg sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-medium text-pblv-ink">Générer le Bon T'POZ</DialogTitle>
            </DialogHeader>
            {bonPdfFor && (
              <form onSubmit={generateBonPdf} className="flex flex-col gap-4">
                <div className="rounded-xl bg-white p-3 text-sm text-pblv-ink">
                  <p className="font-semibold">{bonPdfFor.nom_prenom} — {bonPdfFor.numero_dossier}</p>
                  <p className="text-xs text-pblv-inksoft">{bonPdfFor.partenaire} · {bonPdfFor.experience} · du {bonPdfFor.date_debut} au {bonPdfFor.date_fin}</p>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Type de partenaire</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[["hebergeur", "Hébergeur"], ["bien-etre", "Partenaire bien-être"]].map(([v, l]) => (
                      <label key={v} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-pblv-ink cursor-pointer">
                        <input type="radio" name="pdf_type" data-testid={`bon-pdf-type-${v}`} checked={bonPdfForm.type_partenaire === v} onChange={() => setBonPdfForm((f) => ({ ...f, type_partenaire: v }))} className="h-4 w-4 accent-pblv-mauve" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Modalité applicable</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[["gratuite", "Gratuité"], ["tarif_reduit", "Tarif réduit"], ["remise", "Remise"]].map(([v, l]) => (
                      <label key={v} className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm text-pblv-ink cursor-pointer">
                        <input type="radio" name="pdf_modalite" data-testid={`bon-pdf-modalite-${v}`} checked={bonPdfForm.modalite === v} onChange={() => setBonPdfForm((f) => ({ ...f, modalite: v }))} className="h-4 w-4 accent-pblv-mauve" />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
                {bonPdfForm.modalite === "remise" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Tarif normal (€)</label>
                      <input data-testid="bon-pdf-tarif-normal" value={bonPdfForm.tarif_normal} onChange={(e) => setBonPdfForm((f) => ({ ...f, tarif_normal: e.target.value }))} className={field} placeholder="Ex. : 240" />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Remise (%)</label>
                      <input data-testid="bon-pdf-remise" value={bonPdfForm.remise_pct} onChange={(e) => setBonPdfForm((f) => ({ ...f, remise_pct: e.target.value }))} className={field} placeholder="Ex. : 50" />
                    </div>
                  </div>
                )}
                {bonPdfForm.modalite === "tarif_reduit" && (
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Montant dédié (€)</label>
                    <input data-testid="bon-pdf-montant" value={bonPdfForm.montant_dedie} onChange={(e) => setBonPdfForm((f) => ({ ...f, montant_dedie: e.target.value }))} className={field} placeholder="Ex. : 100" />
                  </div>
                )}
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Reste à charge pour la bénéficiaire (€)</label>
                  <input data-testid="bon-pdf-reste" value={bonPdfForm.reste_a_charge} onChange={(e) => setBonPdfForm((f) => ({ ...f, reste_a_charge: e.target.value }))} className={field} placeholder="Ex. : 120" />
                </div>
                <button
                  type="submit"
                  data-testid="bon-pdf-generate"
                  disabled={bonPdfLoading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-pblv-mauvedark disabled:opacity-60"
                >
                  <Download className="h-4 w-4" />
                  {bonPdfLoading ? "Génération…" : "Générer le Bon PDF"}
                </button>
                <p className="text-xs text-pblv-inksoft">Le PDF est pré-rempli avec la bénéficiaire, le partenaire, les dates, le code de réservation et une validité de 7 jours.</p>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Change password dialog */}
        <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
          <DialogContent aria-describedby={undefined} data-testid="change-password-dialog" className="rounded-3xl bg-pblv-bg sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl font-medium text-pblv-ink">Changer mon mot de passe</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitPassword} className="flex flex-col gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Ancien mot de passe</label>
                <input type="password" required data-testid="pwd-old" value={pwdForm.old_password} onChange={(e) => setPwdForm((f) => ({ ...f, old_password: e.target.value }))} className={field} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Nouveau mot de passe (min. 8 caractères)</label>
                <input type="password" required minLength={8} data-testid="pwd-new" value={pwdForm.new_password} onChange={(e) => setPwdForm((f) => ({ ...f, new_password: e.target.value }))} className={field} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Confirmer le nouveau mot de passe</label>
                <input type="password" required data-testid="pwd-confirm" value={pwdForm.confirm} onChange={(e) => setPwdForm((f) => ({ ...f, confirm: e.target.value }))} className={field} />
              </div>
              <button
                type="submit"
                data-testid="pwd-submit"
                disabled={pwdLoading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-pblv-mauvedark disabled:opacity-60"
              >
                <KeyRound className="h-4 w-4" />
                {pwdLoading ? "Changement…" : "Changer le mot de passe"}
              </button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Partenaires tab */}
        {tab === "partenaires" && (
          <div className="mt-6">
            <div className="mb-4 flex justify-end">
              <button
                data-testid="admin-export-partenaires"
                onClick={() => exportCsv("partenaires")}
                disabled={partenaires.length === 0}
                className="inline-flex items-center gap-2 rounded-full border border-pblv-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-pblv-ink transition-all hover:-translate-y-0.5 hover:bg-pblv-bg disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <Download className="h-4 w-4" /> Exporter en CSV
              </button>
            </div>
            <div className="space-y-3 md:hidden" data-testid="admin-partenaires-cards">
              {partenaires.map((p) => (
                <div key={p.id} data-testid={`admin-partenaire-card-${p.id}`} className="rounded-2xl border border-pblv-ink/10 bg-white p-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-pblv-ink">{p.nom}</p>
                    <span className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-1 text-xs font-semibold ${p.status === "traité" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#a15a63]"}`}>
                      {p.status === "traité" ? "Traité" : "Nouveau"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-pblv-inksoft">
                    {p.role && <p>{p.role}</p>}
                    {p.structure && <p>{p.structure}</p>}
                    <p>{p.email}{p.tel ? ` · ${p.tel}` : ""}</p>
                    {p.message && <p className="italic">« {p.message} »</p>}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 border-t border-pblv-ink/5 pt-3">
                    {p.status !== "traité" && (
                      <button data-testid={`admin-treat-m-${p.id}`} onClick={() => setStatus(p.id, "traité")} className={`${mbtn} border-pblv-ink/10 text-emerald-600 hover:bg-emerald-50`}>
                        <Check className="h-4 w-4" /> Traité
                      </button>
                    )}
                    <button data-testid={`admin-delete-partenaire-m-${p.id}`} onClick={() => deletePartenaire(p.id)} className={`${mbtn} border-red-200 text-red-500 hover:bg-red-50`}>
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
              {partenaires.length === 0 && <p className="rounded-2xl border border-pblv-ink/10 bg-white p-8 text-center text-sm text-pblv-inksoft">Aucune candidature pour le moment.</p>}
            </div>
            <div className="hidden overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white md:block">
            <table className="w-full text-left text-sm" data-testid="admin-partenaires-table">
              <thead className="border-b border-pblv-ink/10 text-xs uppercase tracking-wider text-pblv-inksoft">
                <tr>
                  <th className="px-5 py-3">Nom</th>
                  <th className="hidden px-5 py-3 md:table-cell">Rôle</th>
                  <th className="hidden px-5 py-3 lg:table-cell">Contact</th>
                  <th className="px-5 py-3">Statut</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partenaires.map((p) => (
                  <tr key={p.id} className="border-b border-pblv-ink/5 align-top last:border-0" data-testid={`admin-partenaire-row-${p.id}`}>
                    <td className="px-5 py-3">
                      <p className="font-medium text-pblv-ink">{p.nom}</p>
                      {p.structure && <p className="text-xs text-pblv-inksoft">{p.structure}</p>}
                      {p.message && <p className="mt-1 max-w-xs text-xs italic text-pblv-inksoft">« {p.message} »</p>}
                    </td>
                    <td className="hidden px-5 py-3 text-pblv-inksoft md:table-cell">{p.role}</td>
                    <td className="hidden px-5 py-3 lg:table-cell">
                      <a href={`mailto:${p.email}`} className="text-pblv-mauve hover:underline">{p.email}</a>
                      {p.tel && <p className="text-xs text-pblv-inksoft">{p.tel}</p>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${p.status === "traité" ? "bg-emerald-100 text-emerald-700" : "bg-pblv-rose/25 text-[#a15a63]"}`}>
                        {p.status === "traité" ? "Traité" : "Nouveau"}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        {p.status !== "traité" && (
                          <button
                            data-testid={`admin-treat-${p.id}`}
                            onClick={() => setStatus(p.id, "traité")}
                            className="inline-flex h-8 items-center gap-1 rounded-lg border border-pblv-ink/10 px-2.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50"
                          >
                            <Check className="h-3.5 w-3.5" /> Traité
                          </button>
                        )}
                        <button
                          data-testid={`admin-delete-partenaire-${p.id}`}
                          onClick={() => deletePartenaire(p.id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {partenaires.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-pblv-inksoft">
                      Aucune candidature pour le moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}

        {/* Newsletter tab */}
        {tab === "newsletter" && (
          <div className="mt-6 space-y-6">
            {/* Auto newsletter card */}
            <div
              data-testid="admin-newsletter-auto"
              className="flex flex-col gap-6 rounded-2xl border border-pblv-mauve/20 bg-pblv-mauve/5 p-6 md:flex-row md:items-center md:justify-between md:p-8"
            >
              <div className="flex flex-1 items-start gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-[0_4px_20px_rgba(156,128,155,0.15)]">
                  <Calendar className="h-6 w-6 text-pblv-mauve" strokeWidth={1.8} />
                </div>
                <div>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
                    <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> Envoi automatique
                  </p>
                  <h3 className="mt-1 font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
                    Newsletter mensuelle des nouveaux lieux
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-pblv-inksoft">
                    Chaque <b>1<sup>er</sup> du mois à 9h</b> (Guadeloupe), les nouveaux lieux T'POZ ajoutés depuis le mois précédent sont envoyés automatiquement à tous les abonné·e·s actif·ve·s.
                  </p>
                  {autoInfo && (
                    <div className="mt-3 flex flex-wrap gap-4 text-xs font-semibold text-pblv-inksoft">
                      <span>
                        <span className="text-pblv-mauve">{autoInfo.count}</span> nouveau{autoInfo.count > 1 ? "x" : ""} lieu{autoInfo.count > 1 ? "x" : ""} ce mois
                      </span>
                      <span>·</span>
                      <span>
                        <span className="text-pblv-mauve">{autoInfo.subscribers}</span> abonné·e·s actif·ve·s
                      </span>
                      {autoInfo.next_run && (
                        <>
                          <span>·</span>
                          <span>
                            Prochain envoi :{" "}
                            <span className="text-pblv-mauve">
                              {new Date(autoInfo.next_run).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={sendAutoNow}
                disabled={autoLoading || !autoInfo?.has_content}
                data-testid="admin-newsletter-auto-send"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-pblv-mauve px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <Send className="h-4 w-4" strokeWidth={1.8} />
                {autoLoading ? "Envoi…" : "Envoyer maintenant"}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
            {/* Composer */}
            <form
              onSubmit={sendNewsletter}
              data-testid="admin-newsletter-composer"
              className="rounded-2xl border border-pblv-ink/10 bg-white p-6 lg:col-span-3 md:p-8"
            >
              <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
                <Send className="h-3.5 w-3.5" /> Envoyer une newsletter
              </p>
              <h3 className="mt-2 font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
                Composer et envoyer
              </h3>
              <p className="mt-2 text-sm text-pblv-inksoft">
                L'envoi est instantané aux {subscribers.filter(s => s.active).length} abonné·e·s actif·ve·s.
              </p>

              <div className="mt-6 flex flex-col gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Objet</label>
                  <input
                    type="text"
                    required
                    value={newsletterSubject}
                    onChange={(e) => setNewsletterSubject(e.target.value)}
                    data-testid="newsletter-subject"
                    placeholder="Nouveaux lieux T'POZ — Octobre 2026"
                    className={field}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-pblv-ink">Message</label>
                  <textarea
                    required
                    rows={10}
                    value={newsletterBody}
                    onChange={(e) => setNewsletterBody(e.target.value)}
                    data-testid="newsletter-body"
                    placeholder="Chères T'POZ,&#10;&#10;Ce mois-ci, nous avons le plaisir de vous présenter…"
                    className={field}
                  />
                  <p className="mt-1.5 text-xs text-pblv-inksoft">
                    Astuce : laissez une ligne vide entre les paragraphes.
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={newsletterSending || subscribers.length === 0}
                  data-testid="newsletter-send"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark disabled:opacity-60"
                >
                  {newsletterSending ? "Envoi en cours…" : `Envoyer à ${subscribers.filter(s => s.active).length} abonné·e·s`}
                  <Send className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </div>
            </form>

            {/* Subscribers list */}
            <div className="rounded-2xl border border-pblv-ink/10 bg-white lg:col-span-2">
              <div className="flex items-center justify-between border-b border-pblv-ink/10 p-5">
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
                  <Users className="h-3.5 w-3.5" /> Abonné·e·s ({subscribers.length})
                </p>
                <button
                  onClick={() => exportCsv("newsletter")}
                  disabled={subscribers.length === 0}
                  data-testid="admin-export-newsletter"
                  className="inline-flex items-center gap-1.5 rounded-full border border-pblv-ink/15 px-3 py-1.5 text-xs font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg disabled:opacity-40"
                >
                  <Download className="h-3.5 w-3.5" /> CSV
                </button>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {subscribers.length === 0 && (
                  <p className="p-8 text-center text-sm text-pblv-inksoft">
                    Aucun abonné pour l'instant.
                  </p>
                )}
                {subscribers.map((s) => (
                  <div
                    key={s.id}
                    data-testid={`newsletter-sub-${s.id}`}
                    className="flex items-center justify-between gap-3 border-b border-pblv-ink/5 px-5 py-3.5 last:border-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-pblv-ink">
                        {s.email}
                      </p>
                      {s.prenom && (
                        <p className="text-xs text-pblv-inksoft">{s.prenom}</p>
                      )}
                    </div>
                    <button
                      onClick={() => deleteSubscriber(s.id)}
                      data-testid={`newsletter-del-${s.id}`}
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Témoignages tab */}
        {tab === "temoignages" && (
          <div className="mt-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className="font-serif text-2xl font-medium text-pblv-ink md:text-3xl">Témoignages</h3>
                <p className="mt-1 text-sm text-pblv-inksoft">Gérez les 3 (ou plus) témoignages affichés sur la landing page.</p>
              </div>
              <button
                data-testid="admin-add-testimonial"
                onClick={() => {
                  setEditingTestimonial(null);
                  setTestimonialDialog(true);
                }}
                className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
              >
                <Plus className="h-4 w-4" /> Ajouter un témoignage
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  data-testid={`admin-testimonial-card-${t.id}`}
                  className="overflow-hidden rounded-2xl border border-pblv-ink/10 bg-white"
                >
                  <div className="relative h-40 bg-pblv-bg2">
                    {t.coverImageUrl && (
                      <img src={t.coverImageUrl} alt={t.name} className="h-full w-full object-cover" />
                    )}
                    <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${t.videoUrl ? "bg-emerald-500 text-white" : "bg-white/85 text-pblv-ink backdrop-blur"}`}>
                      {t.videoUrl ? "Vidéo active" : "Vidéo à venir"}
                    </span>
                    {!t.active && (
                      <span className="absolute right-3 top-3 rounded-full bg-pblv-ink/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">Masqué</span>
                    )}
                  </div>
                  <div className="p-5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-pblv-mauve">#{t.order}</p>
                    <p className="mt-1 font-serif text-lg font-medium text-pblv-ink">{t.name}</p>
                    <p className="text-xs text-pblv-inksoft">{t.role}</p>
                    <p className="mt-3 line-clamp-3 text-sm italic text-pblv-inksoft">« {t.quote} »</p>
                    {t.videoUrl && (
                      <a
                        href={t.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-pblv-mauve hover:underline"
                      >
                        <ExternalLink className="h-3 w-3" /> Ouvrir la vidéo
                      </a>
                    )}
                    <div className="mt-4 flex gap-2 border-t border-pblv-ink/5 pt-4">
                      <button
                        data-testid={`admin-edit-testimonial-${t.id}`}
                        onClick={() => {
                          setEditingTestimonial(t);
                          setTestimonialDialog(true);
                        }}
                        className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-pblv-ink/10 py-2 text-xs font-semibold text-pblv-ink transition-colors hover:bg-pblv-bg"
                      >
                        <Pencil className="h-3.5 w-3.5" /> Modifier
                      </button>
                      <button
                        data-testid={`admin-delete-testimonial-${t.id}`}
                        onClick={() => deleteTestimonial(t.id)}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-pblv-ink/10 text-red-500 transition-colors hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {testimonials.length === 0 && (
                <p className="col-span-full rounded-2xl border border-pblv-ink/10 bg-white p-10 text-center text-pblv-inksoft">
                  Aucun témoignage pour l'instant. Cliquez sur « Ajouter un témoignage ».
                </p>
              )}
            </div>
          </div>
        )}

        {/* Activité & QR codes — déplacés en bas du tableau de bord */}
        <MonthlyChart months={monthly} />

        {/* QR Code partageable */}
        <div
          data-testid="admin-qr-card"
          className="mt-8 flex flex-col items-center gap-6 rounded-2xl border border-pblv-ink/10 bg-white p-6 md:flex-row md:p-8"
        >
          <img
            src="/qr-tpoz.png"
            alt="QR Code T'POZ"
            className="h-32 w-32 rounded-xl border border-pblv-ink/10 bg-pblv-bg object-contain md:h-36 md:w-36"
          />
          <div className="flex-1 text-center md:text-left">
            <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-pblv-mauve md:justify-start">
              <QrCode className="h-3.5 w-3.5" /> QR Code T'POZ
            </p>
            <h3 className="mt-2 font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
              À imprimer sur vos flyers
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-pblv-inksoft">
              Ce QR code redirige directement vers la carte des lieux T'POZ. Idéal pour les flyers, affiches et supports papier de l'association.
            </p>
          </div>
          <a
            href="/qr-tpoz.png"
            download="qr-tpoz.png"
            data-testid="admin-qr-download"
            className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
          >
            <Download className="h-4 w-4" /> Télécharger le QR
          </a>
        </div>

        {/* QR Code page Lite */}
        <div
          data-testid="admin-qr-lite-card"
          className="mt-4 flex flex-col items-center gap-6 rounded-2xl border border-pblv-ink/10 bg-white p-6 md:flex-row md:p-8"
        >
          <img
            src="/qr-tpoz-lite.png"
            alt="QR Code page T'POZ Lite"
            className="h-32 w-32 rounded-xl border border-pblv-ink/10 bg-pblv-bg object-contain md:h-36 md:w-36"
          />
          <div className="flex-1 text-center md:text-left">
            <p className="flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-pblv-mauve md:justify-start">
              <QrCode className="h-3.5 w-3.5" /> QR Code page de communication
            </p>
            <h3 className="mt-2 font-serif text-2xl font-medium text-pblv-ink md:text-3xl">
              La page T'POZ en un flash
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-pblv-inksoft">
              Ce QR code redirige vers la page d'accueil T'POZ. Parfait pour vos flyers, affiches et partages WhatsApp.
            </p>
          </div>
          <a
            href="/qr-tpoz-lite.png"
            download="qr-tpoz-lite.png"
            data-testid="admin-qr-lite-download"
            className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
          >
            <Download className="h-4 w-4" /> Télécharger le QR
          </a>
        </div>

      </main>

      <TestimonialDialog
        open={testimonialDialog}
        onOpenChange={setTestimonialDialog}
        initial={editingTestimonial}
        onSaved={refresh}
      />

      <LieuDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={refresh}
      />
    </div>
  );
}
